import * as Sentry from '@sentry/react'
import { useFlag } from '@unleash/proxy-client-react'
import { MessageSquare } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EmailFieldHint } from '@/components/user/EmailFieldHint'
import { API_CONFIG } from '@/config/api'

// Minimum trimmed length for the description field — catches drive-by junk
// ("a", "x", whitespace) without forcing a real user to write paragraphs.
const DESCRIPTION_MIN_LENGTH = 5

// Persist in-progress form input across remounts so retries on parent pages
// (ConnectionProblemPage polling, status flips on AccountStatusPage) don't
// wipe the user's typed message.
const FORM_DRAFT_KEY = 'signin_issue_form_v1'

interface FormDraft {
  email: string
  description: string
  open: boolean
}

function loadDraft(): FormDraft | null {
  try {
    const raw = sessionStorage.getItem(FORM_DRAFT_KEY)
    return raw ? (JSON.parse(raw) as FormDraft) : null
  } catch {
    return null
  }
}

function saveDraft(draft: FormDraft) {
  try {
    sessionStorage.setItem(FORM_DRAFT_KEY, JSON.stringify(draft))
  } catch {
    // sessionStorage may be unavailable; not worth surfacing
  }
}

function clearDraft() {
  try {
    sessionStorage.removeItem(FORM_DRAFT_KEY)
  } catch {
    // see saveDraft
  }
}

/**
 * Lets parent pages initialize their "reporting issue" state synchronously on
 * mount, so they don't briefly show the primary action button before the form
 * notifies them that it's already open (after a remount).
 */
export function isReportSignInFormOpen(): boolean {
  return loadDraft()?.open === true
}

/**
 * Pull a useful message out of a non-OK API response.
 *
 * - 422 from FastAPI returns `{ detail: [{ msg, loc, ... }] }` — surface the first msg
 *   so the user sees "String should have at most 2000 characters" instead of a generic
 *   "Unable to submit" when the cause is fixable client-side.
 * - 4xx/5xx from our handler returns `{ detail: "Invalid API key" }` etc.
 */
async function extractServerError(response: Response): Promise<string | null> {
  try {
    const body = await response.json()
    if (response.status === 422 && Array.isArray(body.detail)) {
      return body.detail[0]?.msg ?? null
    }
    if (typeof body.detail === 'string') {
      return body.detail
    }
  } catch {
    // not JSON; fall through to caller's generic fallback
  }
  return null
}

export interface ErrorContext {
  error_type: string
  account_status?: string
  auth_reason?: string
  auth_source?: string
  url: string
  user_agent: string
  sentry_event_id?: string
  locale?: string
}

interface Props {
  readonly errorContext: ErrorContext
  readonly prefillEmail?: string
  readonly prefillEmailVerified?: boolean
  readonly variant?: 'button' | 'link'
  readonly onOpenChange?: (isOpen: boolean) => void
}

type FormState = 'idle' | 'open' | 'submitting' | 'success' | 'error'

interface TicketResponse {
  ticket_id: string
  ticket_number: number
  ticket_url: string
}

export function ReportSignInIssue({
  errorContext,
  prefillEmail,
  prefillEmailVerified = false,
  variant = 'button',
  onOpenChange,
}: Props) {
  const enabled = useFlag('signin_issue_reporting')
  const { t, i18n } = useTranslation()
  // Restore in-progress draft so the form survives parent re-renders (e.g. retry polling
  // on ConnectionProblemPage) without wiping the user's typed message.
  const draft = loadDraft()
  const [formState, setFormState] = useState<FormState>(draft?.open ? 'open' : 'idle')
  const [email, setEmail] = useState(draft?.email ?? prefillEmail ?? '')
  const [description, setDescription] = useState(draft?.description ?? '')
  const [ticket, setTicket] = useState<TicketResponse | null>(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [validationError, setValidationError] = useState('')

  // Notify the parent if we restored an open form, so its "hide primary action"
  // state stays in sync after a remount.
  useEffect(() => {
    if (draft?.open) onOpenChange?.(true)
    // Run once on mount only — onOpenChange is stable from the parent's perspective.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Persist the draft while the form is open; clear on submit/cancel.
  useEffect(() => {
    if (formState === 'open') {
      saveDraft({ email, description, open: true })
    } else if (formState === 'idle' || formState === 'success') {
      clearDraft()
    }
  }, [formState, email, description])

  if (!enabled) return null

  function openForm() {
    setFormState('open')
    onOpenChange?.(true)
  }

  function closeForm() {
    setFormState('idle')
    setDescription('')
    onOpenChange?.(false)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setValidationError('')

    // Trim description so " " doesn't pass the browser's `required` check, and
    // require enough text to be a useful support ticket.
    const trimmedDescription = description.trim()
    if (trimmedDescription.length < DESCRIPTION_MIN_LENGTH) {
      setValidationError(t('reportSignInIssue.descriptionTooShort', { min: DESCRIPTION_MIN_LENGTH }))
      return
    }

    setFormState('submitting')

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/integrations/support/signin-issue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-EnGen-Public-Key': import.meta.env.VITE_ENGEN_PUBLIC_KEY ?? '',
        },
        body: JSON.stringify({
          email,
          description: trimmedDescription,
          ...errorContext,
          locale: errorContext.locale ?? i18n.language,
        }),
      })

      if (!response.ok) {
        const serverMsg = await extractServerError(response)
        Sentry.captureException(new Error(`Sign-in issue submit failed: HTTP ${response.status}`), {
          extra: { errorContext, status: response.status, serverMsg },
        })
        setErrorMessage(serverMsg ?? t('reportSignInIssue.errorMessage'))
        setFormState('error')
        return
      }

      const data: TicketResponse = await response.json()
      setTicket(data)
      setFormState('success')
    } catch (err) {
      Sentry.captureException(err, { extra: { errorContext } })
      setErrorMessage(t('reportSignInIssue.errorMessage'))
      setFormState('error')
    }
  }

  if (formState === 'idle') {
    if (variant === 'link') {
      return (
        <button
          type='button'
          onClick={openForm}
          className='w-full text-center text-sm text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline'
        >
          {t('reportSignInIssue.linkText')}
        </button>
      )
    }
    return (
      <button
        type='button'
        onClick={openForm}
        className='flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition-colors duration-200 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2'
      >
        <MessageSquare className='h-4 w-4' />
        {t('reportSignInIssue.buttonText')}
      </button>
    )
  }

  if (formState === 'success' && ticket) {
    return (
      <div className='w-full rounded-xl border border-green-100 bg-green-50 p-4 text-sm'>
        <p className='font-medium text-green-800'>{t('reportSignInIssue.successTitle')}</p>
        <p className='mt-1 text-green-700'>{t('reportSignInIssue.successMessage', { email })}</p>
      </div>
    )
  }

  if (formState === 'error') {
    return (
      <div className='w-full rounded-xl border border-red-100 bg-red-50 p-4 text-sm'>
        <p className='text-red-700'>{errorMessage}</p>
        <button
          type='button'
          onClick={() => setFormState('open')}
          className='mt-2 text-red-700 underline underline-offset-2 hover:text-red-900'
        >
          {t('reportSignInIssue.tryAgain')}
        </button>
      </div>
    )
  }

  // 'open' or 'submitting'
  return (
    <form onSubmit={handleSubmit} className='w-full rounded-xl border border-slate-200 bg-slate-50 p-4'>
      <p className='mb-3 text-left text-sm font-medium text-slate-700'>{t('reportSignInIssue.formTitle')}</p>
      <div className='space-y-3'>
        <div>
          <input
            type='email'
            required
            placeholder={t('reportSignInIssue.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className='w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400'
          />
          {prefillEmailVerified && <p className='mt-1 text-xs text-slate-400'>{t('reportSignInIssue.emailHint')}</p>}
          <EmailFieldHint email={email} onAccept={setEmail} />
        </div>
        <div>
          <textarea
            required
            placeholder={t('reportSignInIssue.descriptionPlaceholder')}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value)
              if (validationError) setValidationError('')
            }}
            rows={3}
            maxLength={2000}
            className='w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400'
          />
          {validationError && <p className='mt-1 text-xs text-red-600'>{validationError}</p>}
        </div>
      </div>
      <div className='mt-3 flex gap-2'>
        <button
          type='submit'
          disabled={formState === 'submitting'}
          className='flex-1 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800 disabled:opacity-60'
        >
          {formState === 'submitting' ? t('reportSignInIssue.sending') : t('reportSignInIssue.send')}
        </button>
        <button
          type='button'
          onClick={closeForm}
          disabled={formState === 'submitting'}
          className='w-24 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-60'
        >
          {t('reportSignInIssue.cancel')}
        </button>
      </div>
    </form>
  )
}
